import { Fragment, ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { Employee } from "@/lib/types/Employee";
import { useAppContext } from "@/src/config/AppProvider";
import { useTeamPageContext } from "@/src/features/team/TeamPageContext";
import useSocialMediaIcons from "@/src/utils/helpers/useSocialMediaIcons";

const TeamSection = (): ReactNode => {
  const { employees } = useTeamPageContext();
  const { getSocialMediaIcon } = useSocialMediaIcons();

  return (
    <div className="rbt-team-area bg-color-white rbt-section-gapBottom">
      <div className="container">
        <div className="row row--15 mt_dec--30">
          {employees
            .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
            .map(item => (
              <Fragment key={item.IdGuid}>
                <div className="col-lg-3 col-md-6 col-12 mt--30">
                  <div
                    className="rbt-team-thumbnail "
                    data-bs-toggle="modal"
                    data-bs-target={`#teamDetailModal-${item.IdGuid}`}
                    style={{
                      cursor: "pointer"
                    }}
                  >
                    <div className="rbt-team team-style-default rbt-hover-02">
                      <div
                        className="inner d-flex flex-column flex-fill"
                        style={{
                          minHeight: 400
                        }}
                      >
                        <div className="thumbnail">
                          <Image src={item.ProfileImage} width={415} height={555} priority alt={item.FirstName + " " + item.LastName} />
                        </div>
                        <div className="content d-flex flex-column flex-fill">
                          <h2
                            className="title"
                            style={{
                              fontSize: "20px"
                            }}
                          >
                            {item.FirstName} {item.LastName}
                          </h2>
                          <h6
                            className="subtitle theme-gradient"
                            style={{
                              fontSize: "16px"
                            }}
                          >
                            {item.JobTitle}
                          </h6>
                          <p className="description text-multiline-ellipsis-4 mt--10">{item.Bio}</p>
                          <ul
                            className="social-icon social-default pt--25"
                            style={{
                              marginTop: "auto"
                            }}
                          >
                            <li>
                              <Link href={`mailto:${item.Email}`} title={item.Email}>
                                <i className="feather-mail"></i>
                              </Link>
                            </li>
                            {item.SocialMediaLinks.map(social => (
                              <li key={social.IdGuid}>
                                <Link href={social.PlatformUrl} target="_blank">
                                  {getSocialMediaIcon(social.PlatformName)}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <DetailModal employee={item} />
              </Fragment>
            ))}
        </div>
      </div>
    </div>
  );
};

const DetailModal = ({ employee }: { employee: Employee }): ReactNode => {
  const { licenceSettings } = useAppContext();

  return (
    <div
      className="rbt-team-modal modal fade rbt-modal-default custom-backdrop"
      id={`teamDetailModal-${employee.IdGuid}`}
      tabIndex={-1}
      aria-labelledby={`teamDetailModal-${employee.IdGuid}`}
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <button type="button" className="rbt-round-btn" data-bs-dismiss="modal" aria-label="Close">
              <i className="feather-x"></i>
            </button>
          </div>

          <div className="modal-body">
            <div className="inner">
              <div className="row g-5 row--30 align-items-center">
                <div className="col-lg-4 align-self-baseline">
                  <div className="rbt-team-thumbnail">
                    <div className="thumb">
                      <Image
                        className="w-100"
                        src={employee.ProfileImage}
                        width={415}
                        height={555}
                        priority
                        alt={employee.FirstName + " " + employee.LastName}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-8">
                  <div className="rbt-team-details">
                    <div className="author-info">
                      <h4 className="title">
                        {employee.FirstName} {employee.LastName}
                      </h4>
                      <span className="designation theme-gradient">{employee.JobTitle}</span>
                      <span className="team-form">
                        <i className="feather-map-pin"></i>
                        <span className="location">{licenceSettings.SiteTitle}</span>
                      </span>
                    </div>
                    <p
                      className="mb--15 p-0"
                      style={{
                        lineHeight: "28px"
                      }}
                    >
                      {employee.Bio}
                    </p>
                    <ul className="social-icon social-default mt--20 justify-content-start">
                      {employee.SocialMediaLinks.map(social => (
                        <div className="read-more-btn" key={social.IdGuid}>
                          <Link className="rbt-moderbt-btn" href={social.PlatformUrl} target="_blank">
                            <span className="moderbt-btn-text">{social.PlatformName}</span>
                            <i className="feather-arrow-right"></i>
                          </Link>
                        </div>
                      ))}
                    </ul>
                    <ul className="rbt-information-list mt--25">
                      <li>
                        <Link href={`mailto:${employee.Email}`}>
                          <i className="feather-mail"></i>
                          {employee.Email}
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="top-circle-shape"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamSection;
